import React, { useEffect } from "react"
import styled from "styled-components"
import { AnimatePresence, motion } from "framer-motion"
import { useInView } from "react-intersection-observer"
import { useLocation } from "@reach/router"

import {
  PrismicImage,
  CallToAction,
  StyledHeading,
  BodyCopy,
} from "../../common"

import { VideoBlock } from "./videoBlock"

const StyledPopup = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #ffffff;
  z-index: 999999;

  > div {
    width: 100%;
    height: 100%;
    overflow: scroll;

    .close {
      display: flex;
      padding: 0 0 40px 0;
      margin-left: auto;
      @media (min-width: 1024px) {
        position: absolute;
        top: 43px;
        right: 34px;
      }
    }
  }

  .video-block {
    padding: 0 !important;
  }

  .play-button {
    width: 100% !important;
    left: 0 !important;
  }
`

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column-reverse;
  column-gap: 46px;
  align-items: center;
  justify-content: flex-end;
  height: 100%;
  width: 100%;
  padding: 40px 3vw 0 3vw;

  @media (min-width: 1024px) {
    flex-direction: row;
  }

  div {
    width: 100%;
  }

  .text-wrapper {
    margin-top: 40px;
    padding: 0 0 40px 0;

    @media (min-width: 1024px) {
      margin-top: 0;
      padding: 0;
    }

    h2 {
      font-weight: 400 !important;
    }

    .label {
      color: #15748b;
      font-weight: 700 !important;
      margin: 0;
    }
    .heading {
      color: #15748b !important;
      font-weight: 700 !important;
      margin: 10px 0 20px 0;
    }

    .rich-text {
      margin: 40px 0 0 0;

      p {
        line-height: 30px;
      }
    }
  }

  .img-wrapper > div {
    aspect-ratio: 6/4;
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;

    img,
    .video-block,
    .gatsby-image-wrapper {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  a {
    font-size: ${(props) => props.theme.fontSizes.bodyReg};
    color: ${(props) => props.theme.colors.buttonTextColor};
    background: ${(props) => props.theme.colors.brandColor};
    border-radius: 100rem;
    padding: 0.6rem 2.3rem;
    line-height: 1.5;
    border: none;
    display: flex;
    font-weight: 700;
    margin-top: 1.5rem;
    text-decoration: none;
    cursor: pointer;
    width: fit-content;
  }
`

const CardPopup = ({ blockcolour, setActivePopup, activePopup, content }) => {
  const {
    cardimage,
    featureexcerpt,
    featureheading,
    linktext,
    popuplink,
    popupcontent,
    videourl,
    popuptitle,
    video_thumbnail,
  } = content || {}
  return (
    <StyledPopup
      key={activePopup}
      initial={{ y: "100%" }}
      animate={{ y: 0, transition: { duration: 0.5 } }}
      exit={{ y: "100%", transition: { duration: 0.5 } }}
    >
      <div style={{ backgroundColor: blockcolour ? blockcolour : "#fff" }}>
        <ContentWrapper>
          <div className="text-wrapper">
            <StyledHeading size="h3" className="label">
              {popuptitle?.text}
            </StyledHeading>
            <StyledHeading size="h2" className="heading">
              {featureheading?.text}
            </StyledHeading>
            <StyledHeading size="h3">{featureexcerpt}</StyledHeading>
            <div
              className="rich-text"
              dangerouslySetInnerHTML={{ __html: popupcontent?.html }}
            />
            {popuplink?.link_type === "Web" ? (
              <a href={popuplink?.url} target="_blank" rel="noreferrer">
                {linktext}
              </a>
            ) : (
              <CallToAction
                type={popuplink?.link_type}
                text={linktext}
                page={popuplink}
              />
            )}
          </div>

          <div className="img-wrapper">
            {videourl ? (
              <VideoBlock
                poster_image={video_thumbnail ?? cardimage}
                full_video={{ url: videourl }}
              />
            ) : (
              cardimage && <PrismicImage {...cardimage} />
            )}
          </div>

          <button
            aria-label="close-button"
            className="close"
            onClick={() => setActivePopup(false)}
          >
            <svg
              width="33"
              height="33"
              viewBox="0 0 33 33"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M33 30.6842L30.8 32.8842L16.4421 18.7579L2.2 33L0 30.6842L14.2421 16.4421L0 2.2L2.2 0L16.4421 14.2421L30.6842 0L32.8842 2.2L18.7579 16.4421L33 30.6842Z"
                fill="#15748B"
              />
            </svg>
          </button>
        </ContentWrapper>
      </div>
    </StyledPopup>
  )
}

const StyledWrapper = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 40px 3vw 0px 3vw;

  @media (min-width: 1440px) {
    padding: 83px 3vw 99px 3vw;
  }

  .cta-wrapper {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px 0 50px 0;

    @media (min-width: 1440px) {
      padding: 40px 0 0 0;
    }
  }
`

const StyledIcon = styled.div`
  max-width: 72px;
  width: 100%;
  margin: 0 auto;
`

const StyledBlockText = styled.div`
  text-align: center;
  margin: auto;
  .subtitle {
    max-width: 700px;
    margin: 0 auto;
  }
`

const StyledCardWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  column-gap: 25px;
  row-gap: 15px;
  align-items: center;
  justify-content: center;
  margin: 15px auto 0 auto;
  width: fit-content;

  @media (min-width: 1024px) {
    grid-template-columns: repeat(${(props) => props.cols}, 1fr);
    align-items: flex-start;
  }

  @media (min-width: 1440px) {
    margin-top: 87px;
  }
`

const StyledCard = styled.div`
  max-width: 350px;
  width: 100%;
  background-color: #fff;
  border-radius: 20px;
  height: 100%;
  margin: auto;
`

const ImageWrapper = styled.div`
  .gatsby-image-wrapper {
    aspect-ratio: 4 / 3;
    position: relative;
    width: 100%;
    border-radius: 20px 20px 0 0;
  }

  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const CardText = styled.div`
  padding: 15px;
  text-align: center;
  .card-title {
    color: #005d73 !important;
    margin: 0;
  }
  .excerpt {
    margin: 12px 0 !important;
  }
  button {
    color: #005d73;
    font-size: 15px;
    font-weight: 600;
  }
  span {
    margin-right: 12px;
  }

  svg {
    margin-bottom: 2px;
  }

  @media (min-width: 1440px) {
    padding: 20px;
  }
`

const Card = ({ card, setActivePopup }) => {
  const { search } = useLocation()

  useEffect(() => {
    if (
      search?.replace("?", "") ===
      card?.featureheading?.text?.replaceAll(" ", "-").toLowerCase()
    ) {
      return setActivePopup(card)
    }
  }, [card, search, setActivePopup])

  return (
    <StyledCard>
      <ImageWrapper>
        <PrismicImage {...card?.cardimage} />
      </ImageWrapper>
      <CardText>
        <StyledHeading size="h3" className="card-title">
          {card?.featureheading?.text}
        </StyledHeading>
        <BodyCopy className="excerpt">{card?.featureexcerpt}</BodyCopy>
        <button onClick={() => setActivePopup(card)}>
          <span>{card?.buttontext}</span>
          <svg
            width="15"
            height="7"
            viewBox="0 0 15 7"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M7.44713 -0.000115222C7.2735 0.0123784 7.10918 0.0814869 6.98047 0.196147L0.758241 5.67322C0.677416 5.73856 0.610935 5.81921 0.562794 5.91032C0.514653 6.00143 0.485846 6.10112 0.478105 6.2034C0.470364 6.30568 0.483849 6.40844 0.517749 6.50549C0.551649 6.60254 0.605265 6.69188 0.675372 6.76815C0.745479 6.84442 0.830631 6.90603 0.925708 6.94929C1.02078 6.99255 1.12382 7.01655 1.22863 7.01987C1.33343 7.02318 1.43784 7.00573 1.53558 6.96857C1.63331 6.93141 1.72236 6.87531 1.79735 6.80363L7.50002 1.78298L13.2027 6.80363C13.2777 6.8753 13.3667 6.93141 13.4645 6.96857C13.5622 7.00573 13.6666 7.02318 13.7714 7.01987C13.8762 7.01655 13.9793 6.99254 14.0743 6.94929C14.1694 6.90603 14.2546 6.84442 14.3247 6.76815C14.3948 6.69188 14.4484 6.60254 14.4823 6.50549C14.5162 6.40843 14.5297 6.30568 14.5219 6.2034C14.5142 6.10112 14.4854 6.00143 14.4373 5.91032C14.3891 5.81921 14.3226 5.73856 14.2418 5.67322L8.01958 0.196147C7.94247 0.127597 7.85206 0.0748989 7.75372 0.0411841C7.65539 0.00746982 7.55113 -0.00657446 7.44713 -0.000115222Z"
              fill="#005D73"
            />
          </svg>
        </button>
      </CardText>
    </StyledCard>
  )
}

export const FeatureBlock = ({
  heading,
  icon,
  subtitle,
  items,
  blockcolour,
  blocklink,
  blocklinktext,
  activeColour,
  solidColour,
}) => {
  const [activePopup, setActivePopup] = React.useState(false)

  const isBrowser = () => typeof window !== "undefined"

  const screenWidth = isBrowser() && window.innerWidth

  const { ref, inView } = useInView({
    threshold: screenWidth > 1024 ? 0.75 : 0.3,
    triggerOnce: false,
  })

  useEffect(() => {
    if (activeColour[0] !== blockcolour) {
      return activeColour[1](blockcolour)
    }
  }, [inView, activeColour, blockcolour])

  useEffect(() => {
    solidColour ? activeColour[1](blockcolour) : activeColour[1]("#ffffff")
  }, [activeColour, blockcolour, solidColour])

  //seperate items into groups of 3
  const itemsGrouped = items.reduce((r, a, i) => {
    if (i % 3 === 0) r.push([a])
    else r[r.length - 1].push(a)
    return r
  }, [])

  return (
    <>
      <div
        id={heading?.text?.toLowerCase().replace(" ", "-").replace(".", "")}
        style={{
          backgroundColor: activeColour[0],
          transition: "all .5s ease-in-out",
        }}
      >
        <StyledWrapper>
          {icon && (
            <StyledIcon>
              <PrismicImage {...icon} />
            </StyledIcon>
          )}
          <StyledBlockText ref={ref}>
            <StyledHeading size="h2">{heading?.text}</StyledHeading>
            <BodyCopy size="Medium" className="subtitle">
              {subtitle}
            </BodyCopy>
          </StyledBlockText>

          {itemsGrouped?.map((items, i) => (
            <StyledCardWrapper key={i} cols={items?.length}>
              {items?.map((card, index) => {
                return (
                  <Card
                    key={`featuredCard-${index}`}
                    card={card}
                    setActivePopup={setActivePopup}
                  />
                )
              })}
            </StyledCardWrapper>
          ))}

          <div className="cta-wrapper">
            <CallToAction
              type={blocklink?.link_type}
              text={blocklinktext}
              page={blocklink}
            />
          </div>
        </StyledWrapper>
      </div>

      {/* Card Popup */}
      <AnimatePresence>
        {activePopup !== false && (
          <CardPopup
            content={activePopup}
            blockcolour={blockcolour}
            activePopup={activePopup}
            setActivePopup={setActivePopup}
            subtitle={subtitle}
          />
        )}
      </AnimatePresence>
    </>
  )
}
